/**
 * cliente-pratiche-client.ts — F-005 Phase 4 (IMPL-P4-002).
 *
 * Typed fetch wrappers verso `/api/v1/pratiche/*` (endpoint cliente). Mirror
 * delle convenzioni di `features/pratica/lib/pratica-client.ts`:
 *   - `credentials: 'include'` su ogni richiesta (sessione Better Auth)
 *   - errori non-2xx rilanciati come Error con `{ status, code }` enriched
 *   - funzioni async pure, no side effects
 *
 * NB: `StatoPratica` è ridefinito qui come union literal coerente con
 * l'enum Prisma `PraticaStato` (BOZZA, INVIATA, IN_CARICO, IN_ELABORAZIONE,
 * COMPLETATA, BLOCCATA, ANNULLATA). `ARCHIVIATA` è incluso come futuro stato
 * documentato dal SOURCE_PLAN (F-006) — il map `STATO_META` lo conosce, ma
 * la API non lo restituisce in V1.
 */

/** Stati pratica come definiti dall'enum Prisma `PraticaStato`. */
export type StatoPratica =
  | 'BOZZA'
  | 'INVIATA'
  | 'IN_CARICO'
  | 'IN_ELABORAZIONE'
  | 'COMPLETATA'
  | 'BLOCCATA'
  | 'ANNULLATA'
  | 'ARCHIVIATA';

export interface PraticaListItem {
  id: string;
  stato: StatoPratica;
  codicePratica: string;
  updatedAt: string; // ISO
}

export interface PraticaListResponse {
  items: PraticaListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PraticaDetailResponse {
  pratica: {
    id: string;
    stato: StatoPratica;
    codicePratica: string;
    blockedReasons: string[];
    blockedDetail: string | null;
    datiStrutturati: Record<string, unknown>;
    updatedAt: string;
  };
  timeline: Array<{
    id: string;
    eventType: string;
    actorType: string;
    actorName: string | null;
    payload: Record<string, unknown> | null;
    createdAt: string;
  }>;
}

export interface PraticaStatusResponse {
  id: string;
  stato: StatoPratica;
  updatedAt: string;
}

const BASE = '/api/v1';

interface ApiErrorEnvelope {
  code?: string;
  message?: string;
}

/**
 * Wrapper fetch privato condiviso da tutte le funzioni del client.
 * Includes `credentials: 'include'` per sessione Better Auth, e mappa errori
 * non-2xx in Error con campi extra `status` + `code` (best-effort parse del
 * body JSON).
 */
async function apiFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ApiErrorEnvelope;
    const err = new Error(body.message ?? `HTTP ${res.status}`) as Error & {
      status: number;
      code?: string;
    };
    err.status = res.status;
    err.code = body.code;
    throw err;
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

/**
 * GET /api/v1/pratiche?scope=in-progress&page=…&pageSize=…
 * Lista pratiche in corso del tenant corrente.
 */
export const fetchPratiche = (
  params: { scope?: 'in-progress'; page?: number; pageSize?: number } = {},
): Promise<PraticaListResponse> => {
  const qs = new URLSearchParams();
  if (params.scope) qs.set('scope', params.scope);
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  const suffix = qs.toString();
  return apiFetch<PraticaListResponse>(
    suffix ? `${BASE}/pratiche?${suffix}` : `${BASE}/pratiche`,
  );
};

/**
 * GET /api/v1/pratiche/:id/detail
 * Detail + timeline per la pagina di dettaglio cliente.
 */
export const fetchPraticaDetail = (id: string): Promise<PraticaDetailResponse> =>
  apiFetch<PraticaDetailResponse>(`${BASE}/pratiche/${id}/detail`);

/**
 * GET /api/v1/pratiche/:id/status
 * Proiezione minimale per polling rapido (server emette ETag/304).
 */
export const fetchPraticaStatus = (id: string): Promise<PraticaStatusResponse> =>
  apiFetch<PraticaStatusResponse>(`${BASE}/pratiche/${id}/status`);

/**
 * PATCH /api/v1/pratiche/:id/correzione/bozza
 * Salva una bozza di correzione (datiStrutturati parziali). Phase 3 endpoint.
 */
export const saveCorrezioneBozza = (
  id: string,
  draft: Record<string, unknown>,
): Promise<{ savedAt: string }> =>
  apiFetch<{ savedAt: string }>(`${BASE}/pratiche/${id}/correzione/bozza`, {
    method: 'PATCH',
    body: JSON.stringify({ datiStrutturati: draft }),
  });

/**
 * POST /api/v1/pratiche/:id/correzione
 * Re-submit atomico BLOCCATA→INVIATA con consenso esplicito.
 */
export const submitCorrezione = (
  id: string,
  input: { datiStrutturati: Record<string, unknown>; consent: boolean; pdfS3Key?: string },
): Promise<{ praticaId: string }> =>
  apiFetch<{ praticaId: string }>(`${BASE}/pratiche/${id}/correzione`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
